import { alpha, Theme, Components } from '@mui/material/styles';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { toggleButtonClasses } from '@mui/material/ToggleButton';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { gray, brand } from '../themePrimitives';

/* eslint-disable import/prefer-default-export */
export const inputsCustomizations: Components<Theme> = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: () => ({
        boxSizing: 'border-box',
        transition: 'all 120ms ease-in',
        '&:focus-visible': {
          outline: `3px solid var(--accent-glow)`,
          outlineOffset: '2px',
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: 'none',
        borderRadius: 'var(--radius)',
        textTransform: 'none',
        fontWeight: 500,
        letterSpacing: 0,
        fontSize: 'var(--fs-base)',
        variants: [
          {
            props: { size: 'small' },
            style: { height: 28, padding: '0 10px', fontSize: 'var(--fs-sm)' },
          },
          {
            props: { size: 'medium' },
            style: { height: 36, padding: '0 14px' },
          },
          {
            props: { size: 'large' },
            style: { height: 44, padding: '0 18px' },
          },
          {
            // primary = "ink" button per DESIGN.md §7.1
            props: { color: 'primary', variant: 'contained' },
            style: {
              color: 'var(--surface)',
              backgroundColor: 'var(--ink)',
              '&:hover': { backgroundColor: 'var(--ink-2)' },
              '&:active': { backgroundColor: 'var(--ink)' },
              ...theme.applyStyles('dark', {
                color: 'var(--surface)',
                backgroundColor: 'var(--ink)',
                '&:hover': { backgroundColor: 'var(--border-strong)' },
              }),
            },
          },
          {
            // secondary = surface + border per DESIGN.md §7.1
            props: { variant: 'outlined' },
            style: {
              color: 'var(--ink)',
              border: '1px solid var(--border-strong)',
              backgroundColor: 'var(--surface)',
              '&:hover': { borderColor: 'var(--ink)', backgroundColor: 'var(--surface-2)' },
              '&:active': { backgroundColor: 'var(--surface-3)' },
              ...theme.applyStyles('dark', {
                color: 'var(--ink)',
                border: '1px solid var(--border-strong)',
                backgroundColor: 'var(--surface)',
                '&:hover': { borderColor: 'var(--muted)', backgroundColor: 'var(--surface-2)' },
              }),
            },
          },
          {
            // ghost = transparent per DESIGN.md §7.1
            props: { variant: 'text' },
            style: {
              color: 'var(--ink-2)',
              '&:hover': { backgroundColor: 'var(--surface-2)' },
              '&:active': { backgroundColor: 'var(--surface-3)' },
              ...theme.applyStyles('dark', {
                color: 'var(--ink-2)',
                '&:hover': { backgroundColor: 'var(--surface-2)' },
              }),
            },
          },
          {
            props: { color: 'error', variant: 'contained' },
            style: {
              color: 'var(--danger)',
              backgroundColor: 'var(--danger-bg)',
              '&:hover': { backgroundColor: alpha('#fef2f2', 0.8) },
              ...theme.applyStyles('dark', {
                color: 'var(--danger)',
                backgroundColor: 'var(--danger-bg)',
              }),
            },
          },
        ],
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: 'none',
        borderRadius: 'var(--radius-sm)',
        textTransform: 'none',
        fontWeight: 500,
        letterSpacing: 0,
        color: 'var(--ink-2)',
        border: '1px solid var(--border-strong)',
        backgroundColor: 'var(--surface)',
        '&:hover': { backgroundColor: 'var(--surface-2)', borderColor: 'var(--ink)' },
        '&:active': { backgroundColor: 'var(--surface-3)' },
        ...theme.applyStyles('dark', {
          color: 'var(--ink-2)',
          border: '1px solid var(--border-strong)',
          backgroundColor: 'var(--surface)',
          '&:hover': { backgroundColor: 'var(--surface-2)' },
        }),
        variants: [
          {
            props: { size: 'small' },
            style: {
              width: 28,
              height: 28,
              padding: '4px',
              [`& .${svgIconClasses.root}`]: { fontSize: 14 },
            },
          },
          {
            props: { size: 'medium' },
            style: { width: 36, height: 36 },
          },
        ],
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 'var(--radius-sm)',
        [`& .${toggleButtonGroupClasses.selected}`]: { color: 'var(--accent)' },
        ...theme.applyStyles('dark', {
          [`& .${toggleButtonGroupClasses.selected}`]: { color: 'var(--accent-ink)' },
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '8px 14px',
        textTransform: 'none',
        borderRadius: 'var(--radius-sm)',
        fontWeight: 500,
        ...theme.applyStyles('dark', {
          color: gray[400],
          [`&.${toggleButtonClasses.selected}`]: { color: 'var(--accent-ink)' },
        }),
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: <CheckBoxOutlineBlankRoundedIcon sx={{ color: 'hsla(210, 0%, 0%, 0.0)' }} />,
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,
        borderRadius: 5,
        border: '1px solid var(--border-strong)',
        boxShadow: 'none',
        backgroundColor: 'var(--surface)',
        transition: 'border-color, background-color, 120ms ease-in',
        '&:hover': { borderColor: 'var(--accent)' },
        '&.Mui-focusVisible': {
          outline: `3px solid var(--accent-glow)`,
          outlineOffset: '2px',
          borderColor: 'var(--accent)',
        },
        '&.Mui-checked': {
          color: 'white',
          backgroundColor: 'var(--accent)',
          borderColor: 'var(--accent)',
          boxShadow: 'none',
          '&:hover': { backgroundColor: brand[700] },
        },
        ...theme.applyStyles('dark', {
          borderColor: 'var(--border-strong)',
          backgroundColor: 'var(--surface)',
          '&:hover': { borderColor: 'var(--accent)' },
          '&.Mui-focusVisible': {
            borderColor: 'var(--accent)',
            outline: `3px solid var(--accent-glow)`,
            outlineOffset: '2px',
          },
        }),
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: { border: 'none' },
      input: {
        '&::placeholder': {
          opacity: 0.7,
          color: 'var(--muted-2)',
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: { padding: 0 },
      root: ({ theme }) => ({
        padding: '8px 12px',
        color: 'var(--ink)',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border-strong)',
        backgroundColor: 'var(--surface)',
        fontSize: 'var(--fs-base)',
        transition: 'border 120ms ease-in',
        '&:hover': { borderColor: 'var(--muted)' },
        [`&.${outlinedInputClasses.focused}`]: {
          outline: 'none',
          borderColor: 'var(--accent)',
          boxShadow: 'var(--shadow-glow)',
        },
        ...theme.applyStyles('dark', {
          '&:hover': { borderColor: 'var(--border-strong)' },
        }),
        variants: [
          { props: { size: 'small' }, style: { height: 32 } },
          { props: { size: 'medium' }, style: { height: 40 } },
        ],
      }),
      notchedOutline: { border: 'none' },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: () => ({ color: 'var(--muted)' }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        typography: theme.typography.caption,
        marginBottom: 8,
        color: 'var(--muted)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 500,
      }),
    },
  },
};
